import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement, PointElement, LineElement);

export default function AdminAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('vi_token');
  const api = axios.create({ baseURL: '/api', headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    api.get('/admin/analytics').then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner"></div></div>;
  if (!data) return <div className="container" style={{ padding: 40 }}>Errore caricamento dati</div>;

  const funnelData = {
    labels: data.funnel.map(f => f.label),
    datasets: [{
      label: 'Utenti',
      data: data.funnel.map(f => f.count),
      backgroundColor: ['#3b82f6', '#1a56db', '#16a34a', '#22c55e', '#f59e0b', '#dc2626', '#8b5cf6'],
      borderRadius: 6
    }]
  };

  const dailyData = {
    labels: data.dailyRegs.map(d => d.data),
    datasets: [{
      label: 'Registrazioni',
      data: data.dailyRegs.map(d => d.count),
      borderColor: '#1a56db',
      backgroundColor: 'rgba(26,86,219,0.1)',
      fill: true,
      tension: 0.4
    }]
  };

  const topPagesData = {
    labels: data.topPages.map(p => p.pagina || 'N/D'),
    datasets: [{
      data: data.topPages.map(p => p.count),
      backgroundColor: ['#3b82f6', '#16a34a', '#f59e0b', '#dc2626', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#6366f1', '#84cc16']
    }]
  };

  return (
    <div className="admin-layout">
      <div className="admin-sidebar">
        <h3 style={{ marginBottom: 16, fontSize: 16 }}>Admin Panel</h3>
        <Link to="/admin" style={{ display: 'block', padding: '10px 16px', color: '#9ca3af', borderRadius: 8, marginBottom: 4, fontSize: 14 }}>Dashboard</Link>
        <Link to="/admin" style={{ display: 'block', padding: '10px 16px', color: '#9ca3af', borderRadius: 8, marginBottom: 4, fontSize: 14 }}>CRM Contatti</Link>
        <Link to="/admin/analytics" style={{ display: 'block', padding: '10px 16px', color: 'white', background: 'rgba(255,255,255,0.1)', borderRadius: 8, marginBottom: 4, fontSize: 14 }}>Analytics</Link>
      </div>

      <div className="admin-content">
        <h2 style={{ marginBottom: 24 }}>Analytics e Funnel Tracking</h2>

        {/* Funnel */}
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Funnel di Conversione</h3>
          <div style={{ maxHeight: 400 }}>
            <Bar data={funnelData} options={{
              responsive: true,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true } }
            }} />
          </div>
        </div>

        <div className="grid-2" style={{ marginBottom: 24 }}>
          {/* Daily Registrations */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Registrazioni Giornaliere (30gg)</h3>
            <Line data={dailyData} options={{
              responsive: true,
              plugins: { legend: { display: false } },
              scales: { y: { beginAtZero: true } }
            }} />
          </div>

          {/* Top Pages */}
          <div className="card">
            <h3 style={{ marginBottom: 16 }}>Pagine Piu Visitate</h3>
            <Doughnut data={topPagesData} options={{
              responsive: true,
              plugins: { legend: { position: 'bottom', labels: { font: { size: 11 } } } }
            }} />
          </div>
        </div>

        {/* Funnel Table */}
        <div className="card" style={{ marginBottom: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Dettaglio Funnel</h3>
          <div className="table-container">
            <table>
              <thead><tr><th>Step</th><th>Evento</th><th>Utenti Unici</th><th>Tasso Conversione</th></tr></thead>
              <tbody>
                {data.funnel.map((f, i) => {
                  const prevCount = i > 0 ? data.funnel[i-1].count : f.count;
                  const rate = prevCount > 0 ? ((f.count / prevCount) * 100).toFixed(1) : '100.0';
                  return (
                    <tr key={i}>
                      <td>{i + 1}</td>
                      <td>{f.label}</td>
                      <td><strong>{f.count}</strong></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 100, height: 8, background: '#e5e7eb', borderRadius: 4, overflow: 'hidden' }}>
                            <div style={{ width: `${rate}%`, height: '100%', background: parseFloat(rate) > 50 ? '#16a34a' : parseFloat(rate) > 20 ? '#f59e0b' : '#dc2626', borderRadius: 4 }}></div>
                          </div>
                          <span style={{ fontSize: 13 }}>{rate}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Users Table */}
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Ultimi Utenti ({data.users.length})</h3>
          <div className="table-container">
            <table>
              <thead><tr><th>Email</th><th>Nome</th><th>Azienda</th><th>Regione</th><th>Visura</th><th>Report</th><th>Data</th></tr></thead>
              <tbody>
                {data.users.slice(0, 20).map(u => (
                  <tr key={u.id}>
                    <td>{u.email}</td>
                    <td>{u.nome} {u.cognome}</td>
                    <td>{u.azienda}</td>
                    <td>{u.regione}</td>
                    <td>{u.visura_path ? '&#10003;' : '-'}</td>
                    <td>{u.report_sbloccato ? <span className="status status-qualificato">Sbloccato</span> : u.report_path ? <span className="status status-contattato">Generato</span> : '-'}</td>
                    <td>{new Date(u.created_at).toLocaleDateString('it-IT')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
